import { ThemeProvider } from '@material-ui/styles';
import './css/index.css';
import './css/ui-new.css';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { setAuthTokenGetter, setAuthTokenRefresher } from '@up2rent/graphql';
import { getUp2rentTheme } from '@up2rent/ui';
import { Toaster, ZIndex } from '@up2rent/ui-new';
import isNil from 'lodash/isNil';
// eslint-disable-next-line import/no-unresolved
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import WebFontLoader from 'webfontloader';

import App from './App';
import { guardDomMutationsAgainstBrowserTranslation } from './browserTranslationDomGuard';
import CommunicationAttachmentsDownloadPage from './components/Communications/CommunicationAttachmentsDownloadPage';
import ErrorBoundary from './ErrorBoundary';
import { ConnectionProvider } from './hooks/ConnectionContext';
import EntityConditionalHooks from './hooks/EntityConditionalHooks';
import { FiltersContextProvider } from './hooks/FiltersContext';
import { getAuthToken, refreshAuthToken, UserProvider } from './hooks/UserContext';
import { LocaleProvider } from './i18n/IntlProviderWrapper';
import * as serviceWorker from './serviceWorker';
import { getAppVersion, getCurrentEnv, getSentryEnvironment } from './utils/envUtils';
import { isMarkerIoWidgetError } from './utils/sentryFilterUtils';
import { WithGoogleApi } from './WithGoogleApi';

// eslint-disable-next-line import/extensions
import 'src/components/RentalUnits/Details/Publication/BlinkingArrow.css';

guardDomMutationsAgainstBrowserTranslation();

WebFontLoader.load({
  google: {
    families: ['Mulish:300,400,500,600,700,800,900', 'Material Icons'],
  },
});

/* Bug Tracking */
const IS_DEVELOP = process.env.NODE_ENV === 'development';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const MARKER_IO_STORAGE_QUOTA_ERROR = /QuotaExceededError[\s\S]*'mkr-sr-/;
if (!IS_DEVELOP) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [Sentry.replayIntegration()],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    release: `up2rent@${getAppVersion()}`,
    environment: getSentryEnvironment(),
    ignoreErrors: ['Message could not be passed (timeout)', MARKER_IO_STORAGE_QUOTA_ERROR],
    beforeSend: (event) => (isMarkerIoWidgetError(event) ? null : event),
  });
}

window.Marker?.setCustomData({ version: getAppVersion(), stage: getCurrentEnv() });

setAuthTokenGetter(getAuthToken);
setAuthTokenRefresher(refreshAuthToken);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

const UP2RENT_THEME = getUp2rentTheme();
const APP_THEME = { ...UP2RENT_THEME, zIndex: { ...UP2RENT_THEME.zIndex, snackbar: ZIndex.SNACKBAR } };

/* PDF */
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const container = document.getElementById('root');
const root = createRoot(container!);

const communicationAttachmentsDownloadMatch = window.location.pathname.match(/^\/r\/([^/]+)\/?$/);
if (!isNil(communicationAttachmentsDownloadMatch)) {
  const robotsMeta = document.createElement('meta');
  robotsMeta.name = 'robots';
  robotsMeta.content = 'noindex, nofollow, noarchive';
  document.head.appendChild(robotsMeta);
  root.render(
    <ErrorBoundary>
      <ThemeProvider theme={APP_THEME}>
        <CommunicationAttachmentsDownloadPage communicationId={communicationAttachmentsDownloadMatch[1]} />
      </ThemeProvider>
    </ErrorBoundary>
  );
} else {
  root.render(
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={APP_THEME}>
          {/* @ts-ignore */}
          <BrowserRouter>
            <UserProvider>
              <ConnectionProvider>
                <FiltersContextProvider>
                  <LocaleProvider>
                    <EntityConditionalHooks>
                      <WithGoogleApi>
                        <App />
                      </WithGoogleApi>
                    </EntityConditionalHooks>
                  </LocaleProvider>
                </FiltersContextProvider>
              </ConnectionProvider>
            </UserProvider>
            <Toaster />
          </BrowserRouter>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
